import { Injectable } from '@angular/core';

import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { AssertService } from '@connect-services/client/services/assert.service';

type EditModeStatePayload = Readonly<{ isEditModeActive: boolean }>;
type SessionContextPayload = Readonly<{ sessionId: string }>;

export type TableBroadcastExternalHandlers = Readonly<{
    onCloseRequested: () => void;
    onEditModeStateReceived: (isEditModeActive: boolean) => void;
    onSessionIdReceived: (sessionId: string) => void;
}>;

export type TableBroadcastHomeHandlers = Readonly<{
    getEditModeActive: () => boolean;
    getSessionId: () => string | null;
    onEntitiesSaved: () => Promise<void>;
    onRequestSetEditMode: (isEditModeActive: boolean) => void;
}>;

@Injectable({
    providedIn: 'root',
})
export class TableBroadcastService {
    private channel: BroadcastChannel | null = null;

    public closeChannel(): void {
        if (!this.channel) {
            return;
        }

        this.channel.close();
        this.channel = null;
    }

    public initForExternalView(handlers: TableBroadcastExternalHandlers): void {
        this.openChannelIfNeeded();
        this.registerExternalListener(handlers);
    }

    public initForHomeView(handlers: TableBroadcastHomeHandlers): void {
        this.openChannelIfNeeded();
        this.registerHomeListener(handlers);
    }

    public requestEditModeStateFromHome(): void {
        this.postMessage<null>(
            BroadcastChannelMessageEnum.REQUEST_EDIT_MODE_STATE,
            BroadcastChannelSourceEnum.LAYER,
            null,
        );
    }

    public requestSessionContextFromHome(): void {
        this.postMessage<null>(
            BroadcastChannelMessageEnum.REQUEST_SESSION_CONTEXT,
            BroadcastChannelSourceEnum.LAYER,
            null,
        );
    }

    public sendRequestSetEditModeToHome(isEditModeActive: boolean): void {
        this.postMessage<EditModeStatePayload>(
            BroadcastChannelMessageEnum.REQUEST_SET_EDIT_MODE,
            BroadcastChannelSourceEnum.LAYER,
            { isEditModeActive },
        );
    }

    public syncEditModeToExternal(isEditModeActive: boolean): void {
        this.postMessage<EditModeStatePayload>(
            BroadcastChannelMessageEnum.SYNC_EDIT_MODE,
            BroadcastChannelSourceEnum.HOMEPAGE,
            { isEditModeActive },
        );
    }

    private applyIfPayloadValid<T>(
        additionalData: unknown,
        reader: (data: unknown) => T | null,
        applier: (payload: T) => void,
    ): void {
        const payload = reader(additionalData);
        if (!payload) {
            return;
        }

        applier(payload);
    }

    private applyReplyEditModeStateToExternal(
        data: BroadcastChannelEvent<unknown>,
        handlers: TableBroadcastExternalHandlers,
    ): void {
        this.applyIfPayloadValid(
            data.additionalData,
            (raw) => this.readEditModeStatePayload(raw),
            (payload) => handlers.onEditModeStateReceived(payload.isEditModeActive),
        );
    }

    private applyReplySessionContextToExternal(
        data: BroadcastChannelEvent<unknown>,
        handlers: TableBroadcastExternalHandlers,
    ): void {
        this.applyIfPayloadValid(
            data.additionalData,
            (raw) => this.readSessionContextPayload(raw),
            (payload) => handlers.onSessionIdReceived(payload.sessionId),
        );
    }

    private applyRequestSetEditModeToHome(
        data: BroadcastChannelEvent<unknown>,
        handlers: TableBroadcastHomeHandlers,
    ): void {
        this.applyIfPayloadValid(
            data.additionalData,
            (raw) => this.readEditModeStatePayload(raw),
            (payload) => handlers.onRequestSetEditMode(payload.isEditModeActive),
        );
    }

    private applySyncedEditModeToExternal(
        data: BroadcastChannelEvent<unknown>,
        handlers: TableBroadcastExternalHandlers,
    ): void {
        this.applyIfPayloadValid(
            data.additionalData,
            (raw) => this.readEditModeStatePayload(raw),
            (payload) => handlers.onEditModeStateReceived(payload.isEditModeActive),
        );
    }

    private handleExternalMessage(
        data: BroadcastChannelEvent<unknown>,
        handlers: TableBroadcastExternalHandlers,
    ): void {
        const message = data.message;

        if (!message) {
            return;
        }

        switch (message) {
            case BroadcastChannelMessageEnum.CLOSE:
                handlers.onCloseRequested();
                break;

            case BroadcastChannelMessageEnum.SYNC_EDIT_MODE:
                this.applySyncedEditModeToExternal(data, handlers);
                break;

            case BroadcastChannelMessageEnum.REPLY_EDIT_MODE_STATE:
                this.applyReplyEditModeStateToExternal(data, handlers);
                break;

            case BroadcastChannelMessageEnum.REPLY_SESSION_CONTEXT:
                this.applyReplySessionContextToExternal(data, handlers);
                break;

            default:
                break;
        }
    }

    private async handleHomeMessage(
        data: BroadcastChannelEvent<unknown>,
        handlers: TableBroadcastHomeHandlers,
    ): Promise<void> {
        const message = data.message;

        if (!message) {
            return;
        }

        switch (message) {
            case BroadcastChannelMessageEnum.ENTITIES_SAVED:
                await handlers.onEntitiesSaved();
                break;

            case BroadcastChannelMessageEnum.REQUEST_EDIT_MODE_STATE:
                this.replyEditModeStateToExternal(handlers);
                break;

            case BroadcastChannelMessageEnum.REQUEST_SESSION_CONTEXT:
                this.replySessionContextToExternal(handlers);
                break;

            case BroadcastChannelMessageEnum.REQUEST_SET_EDIT_MODE:
                this.applyRequestSetEditModeToHome(data, handlers);
                break;

            default:
                break;
        }
    }

    private openChannelIfNeeded(): void {
        if (this.channel) {
            return;
        }

        this.channel = new BroadcastChannel(BroadcastChannelNameEnum.LAYERS);
    }

    private postMessage<T>(
        message: BroadcastChannelMessageEnum,
        source: BroadcastChannelSourceEnum,
        additionalData: T,
    ): void {
        if (!this.channel) {
            return;
        }

        this.channel.postMessage({
            additionalData,
            isClosing: false,
            message,
            source,
        } satisfies BroadcastChannelEvent<T>);
    }

    private readEditModeStatePayload(additionalData: unknown): EditModeStatePayload | null {
        try {
            AssertService.isObject(additionalData);

            const isEditModeActive = additionalData['isEditModeActive'];

            if (typeof isEditModeActive !== 'boolean') {
                return null;
            }

            return { isEditModeActive };
        } catch {
            return null;
        }
    }

    private readSessionContextPayload(additionalData: unknown): SessionContextPayload | null {
        try {
            AssertService.isObject(additionalData);

            const sessionId = additionalData['sessionId'];

            if (typeof sessionId !== 'string' || !sessionId.length) {
                return null;
            }

            return { sessionId };
        } catch {
            return null;
        }
    }

    private registerExternalListener(handlers: TableBroadcastExternalHandlers): void {
        if (!this.channel) {
            return;
        }

        this.channel.onmessage = (event: MessageEvent<BroadcastChannelEvent<unknown>>): void => {
            this.handleExternalMessage(event.data, handlers);
        };
    }

    private registerHomeListener(handlers: TableBroadcastHomeHandlers): void {
        if (!this.channel) {
            return;
        }

        this.channel.onmessage = async (event: MessageEvent<BroadcastChannelEvent<unknown>>): Promise<void> => {
            await this.handleHomeMessage(event.data, handlers);
        };
    }

    private replyEditModeStateToExternal(handlers: TableBroadcastHomeHandlers): void {
        const isEditModeActive = handlers.getEditModeActive();

        this.postMessage<EditModeStatePayload>(
            BroadcastChannelMessageEnum.REPLY_EDIT_MODE_STATE,
            BroadcastChannelSourceEnum.HOMEPAGE,
            { isEditModeActive },
        );
    }

    private replySessionContextToExternal(handlers: TableBroadcastHomeHandlers): void {
        const sessionId = handlers.getSessionId();

        if (!sessionId) {
            return;
        }

        this.postMessage<SessionContextPayload>(
            BroadcastChannelMessageEnum.REPLY_SESSION_CONTEXT,
            BroadcastChannelSourceEnum.HOMEPAGE,
            { sessionId },
        );
    }
}
